import { Component, Input, Output, EventEmitter, ViewChild, AfterViewInit, OnChanges, OnInit, OnDestroy } from '@angular/core';
import { SharedModule } from '@Shared/shared.module';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { CommonModule } from '@angular/common';
import { SelectionModel } from '@angular/cdk/collections';
import { SubSink } from 'subsink';
import { AssignedCaseTableRow } from '../assigned-cases-table/assigned-cases-table.component';
import { getSlaStatusFromDueDates } from '@Utils/sla-utils';
import { getTaskDueStatus } from '@Utils/task-due-utils';

@Component({
    selector: 'account-workload-table',
    templateUrl: './account-workload-table.component.html',
    styleUrl: './account-workload-table.component.scss',
    imports: [SharedModule, MatTableModule, MatButtonModule, MatIconModule, CommonModule]
})
export class AccountWorkloadTableComponent implements OnChanges, AfterViewInit, OnInit, OnDestroy {
    /** Full unfiltered flat case list — used for unfiltered per-account SLA counts. */
    @Input() allCases: AssignedCaseTableRow[] = [];
    /** Fully filtered flat case list — used for filtered per-account SLA counts. */
    @Input() filteredCases: AssignedCaseTableRow[] = [];
    /** Full unfiltered flat task list — used for unfiltered per-account task counts. */
    @Input() allTasks: any[] = [];
    /** Fully filtered flat task list — used for filtered per-account task counts. */
    @Input() filteredTasks: any[] = [];
    @Input() isFiltered: boolean = false;
    @Input() activeFilterCount: number = 0;
    @Input() tasksEnabled: boolean = false;
    @Output() selectedAccountsChange = new EventEmitter<any[]>();

    private _sort: MatSort;
    private _paginator: MatPaginator;
    private subs = new SubSink();
    private _isReselecting = false;

    @ViewChild(MatSort)
    set sort(sort: MatSort) {
        this._sort = sort;
        this.assignTableFeatures();
    }
    get sort(): MatSort {
        return this._sort;
    }

    @ViewChild(MatPaginator)
    set paginator(paginator: MatPaginator) {
        this._paginator = paginator;
        this.assignTableFeatures();
    }
    get paginator(): MatPaginator {
        return this._paginator;
    }

    displayedColumns: string[] = ['selection', 'Account_Name', 'Cases'];
    teamCasesByAccountData = new MatTableDataSource<any>();
    selection = new SelectionModel<any>(true, []);

    ngOnInit(): void {
        this.subs.sink = this.selection.changed.subscribe(() => {
            if (!this._isReselecting) {
                this.selectedAccountsChange.emit(this.selection.selected);
            }
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    ngOnChanges(): void {
        this.updateDisplayedColumns();
        if (!this.allCases) {
            return;
        }
        this.teamCasesByAccountData = new MatTableDataSource<any>(this.buildAccountRows());
        this.assignTableFeatures();
        this.reselectRows();
    }

    private updateDisplayedColumns(): void {
        const columns = ['selection', 'Account_Name', 'Cases'];
        if (this.tasksEnabled) {
            columns.push('Tasks');
        }
        this.displayedColumns = columns;
    }

    ngAfterViewInit(): void {
        this.assignTableFeatures();
    }

    private buildAccountRows(): any[] {
        const accountMap = new Map<string, any>();

        const ensureRow = (key: string, accountName: string) => {
            if (!accountMap.has(key)) {
                accountMap.set(key, {
                    parentAccountId: key,
                    accountName: accountName,
                    totalCases: 0,
                    pastDue: 0,
                    dueToday: 0,
                    dueTomorrow: 0,
                    totalTasks: 0,
                    tasksPastDue: 0,
                    tasksDueToday: 0,
                    tasksDueTomorrow: 0,
                });
            }
            return accountMap.get(key);
        };

        // Step 1: full list of cases using allCases
        for (const c of this.allCases) {
            const key = c.parentAccountId;
            if (!key) continue;
            const row = ensureRow(key, c.account);
            row.totalCases++;
            const dates = (c.queue || []).map(q => q.dueDate).filter(d => !!d);
            const statuses = getSlaStatusFromDueDates(dates);
            if (statuses.includes('pastDue')) row.pastDue++;
            if (statuses.includes('dueToday')) row.dueToday++;
            if (statuses.includes('dueTomorrow')) row.dueTomorrow++;
        }

        // Ensure accounts that only have tasks still get a row
        for (const t of this.allTasks) {
            if (!t.parentAccountId) continue;
            ensureRow(t.parentAccountId, t.account);
        }

        // Aggregate unfiltered tasks by account
        for (const t of this.allTasks) {
            const row = accountMap.get(t.parentAccountId);
            if (!row) continue;
            row.totalTasks++;
            const status = getTaskDueStatus(t.followUpDate);
            if (status === 'pastDue') row.tasksPastDue++;
            else if (status === 'dueToday') row.tasksDueToday++;
            else if (status === 'dueTomorrow') row.tasksDueTomorrow++;
        }

        // Step 2: now look at filteredCases/filteredTasks to get counts without removing any accounts that have 0 cases due to filtering
        if (this.isFiltered) {
            for (const row of accountMap.values()) {
                row.totalCases = 0;
                row.pastDue = 0;
                row.dueToday = 0;
                row.dueTomorrow = 0;
                row.totalTasks = 0;
                row.tasksPastDue = 0;
                row.tasksDueToday = 0;
                row.tasksDueTomorrow = 0;
            }
            for (const c of this.filteredCases) {
                const row = accountMap.get(c.parentAccountId);
                if (!row) continue;
                row.totalCases++;
                const dates = (c.queue || []).map(q => q.dueDate).filter(d => !!d);
                const statuses = getSlaStatusFromDueDates(dates);
                if (statuses.includes('pastDue')) row.pastDue++;
                if (statuses.includes('dueToday')) row.dueToday++;
                if (statuses.includes('dueTomorrow')) row.dueTomorrow++;
            }
            for (const t of this.filteredTasks) {
                const row = accountMap.get(t.parentAccountId);
                if (!row) continue;
                row.totalTasks++;
                const status = getTaskDueStatus(t.followUpDate);
                if (status === 'pastDue') row.tasksPastDue++;
                else if (status === 'dueToday') row.tasksDueToday++;
                else if (status === 'dueTomorrow') row.tasksDueTomorrow++;
            }
        }

        return Array.from(accountMap.values());
    }

    assignTableFeatures(): void {
        if (this.teamCasesByAccountData && this._sort && this._paginator) {
            this.teamCasesByAccountData.sort = this._sort;
            this.teamCasesByAccountData.sortingDataAccessor = (item, property) => {
                switch (property) {
                    case 'Account_Name': return (item.accountName ?? '').toLowerCase();
                    case 'Cases': return item.totalCases ?? 0;
                    case 'Tasks': return item.totalTasks ?? 0;
                    case 'Past_Due': return item.pastDue ?? 0;
                    case 'Due_Today': return item.dueToday ?? 0;
                    case 'Due_Tomorrow': return item.dueTomorrow ?? 0;
                    default: return '';
                }
            };
            this.teamCasesByAccountData.paginator = this._paginator;
        }
    }

    reselectRows(): void {
        this._isReselecting = true;
        const selectedIds = this.selection.selected.map(row => row.parentAccountId);
        this.selection.clear();
        if (selectedIds.length > 0) {
            const toReselect = this.teamCasesByAccountData.data.filter(row => selectedIds.includes(row.parentAccountId));
            toReselect.forEach(item => this.selection.select(item));
        }
        this._isReselecting = false;
    }
}
